import {
  BillingPeriod,
  PaidPlanId,
  PlanId,
  Site,
  formatPrice,
  getSiteFeatures,
  getSitePricing,
} from '@ritabygglov/core';
import { IntlShape } from 'react-intl';
import { Locale } from '../../i18n';
import { siteConfig } from '../../config/siteConfig';

export type { BillingPeriod, PaidPlanId };

export type Plan = {
  planId: PlanId;
  title: string;
  yearlyPrice: string;
  monthlyPrice: string;
  monthlyEquivalentPrice: string | null;
  features: {
    label: string;
    checked: boolean;
  }[];
  description: string;
  isPremium: boolean;
  colorScheme: 'lightBlue' | 'blue' | 'black';
  isFree: boolean;
};

const COLOR_SCHEME: Record<PlanId, Plan['colorScheme']> = {
  free: 'lightBlue',
  regular: 'blue',
  professional: 'black',
};

/**
 * Plans, prices and features all come from the shared catalogue in core, so the
 * app and the marketing site can never disagree about what a site sells.
 */
export const getPlans = (
  intl: IntlShape,
  site: Site = siteConfig.site
): Plan[] => {
  const { currency, plans } = getSitePricing(site);
  const locale = intl.locale as Locale;
  const siteFeatures = getSiteFeatures(site);
  const format = (amount: number) => formatPrice(amount, currency, locale);

  return plans.map((plan) => ({
    planId: plan.planId,
    title: intl.formatMessage({ id: `plans.${plan.planId}.title` }),
    monthlyPrice: format(plan.monthly),
    yearlyPrice: format(plan.yearly),
    monthlyEquivalentPrice: plan.yearly
      ? format(Math.floor(plan.yearly / 12))
      : null,
    features: siteFeatures.map((feature) => ({
      label: intl.formatMessage({ id: `plans.feature.${feature}` }),
      checked: plan.features.includes(feature),
    })),
    description: intl.formatMessage(
      { id: `plans.${plan.planId}.description` },
      { appName: siteConfig.appName }
    ),
    isPremium: Boolean(plan.highlight),
    colorScheme: COLOR_SCHEME[plan.planId],
    isFree: plan.planId === 'free',
  }));
};
